import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const signupSchema = z
  .object({
    firstName: z.string().min(1, "Le prénom est requis"),
    lastName: z.string().min(1, "Le nom est requis"),
    password: z.string().min(8, "Le mot de passe doit contenir au moins 8 caractères"),
    confirmPassword: z.string().min(1, "Veuillez confirmer le mot de passe"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

type SignupFormValues = z.infer<typeof signupSchema>;

interface InvitationSignupFormProps {
  email: string; // Full email for auto-login after signup
  emailRedacted: string;
  invitationId: string;
  onSuccess: (organizationId: string) => void;
  onError: (message: string) => void;
}

export function InvitationSignupForm({
  email,
  emailRedacted,
  invitationId,
  onSuccess,
  onError,
}: InvitationSignupFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const acceptWithSignupMutation = api.useMutation("post", "/invitations/{id}/accept-with-signup");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: SignupFormValues) => {
    setIsLoading(true);

    try {
      // 1. Create account and accept invitation
      const result = await acceptWithSignupMutation.mutateAsync({
        params: {
          path: { id: invitationId },
        },
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
        },
      });

      if ("error" in result) {
        onError(result.message || "Erreur lors de la création du compte");
        setIsLoading(false);
        return;
      }

      // 2. Auto-login with the credentials we just used
      const loginResult = await authClient.signIn.email({
        email,
        password: data.password,
      });

      if (loginResult.error) {
        // Account was created but login failed - still redirect to login page
        onError("Compte créé mais connexion automatique échouée. Veuillez vous connecter.");
        setIsLoading(false);
        return;
      }

      // 3. Set active organization
      await authClient.organization.setActive({
        organizationId: result.organizationId,
      });

      onSuccess(result.organizationId);
    } catch (error) {
      onError((error as Error).message || "Erreur inattendue");
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {/* Email display (redacted, non-editable) */}
          <div className="grid gap-2">
            <label className="text-sm font-medium">Email</label>
            <Input value={emailRedacted} disabled className="bg-muted" />
          </div>

          {/* First name */}
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>Prénom</FormLabel>
                <FormControl>
                  <Input autoFocus autoComplete="given-name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Last name */}
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>Nom</FormLabel>
                <FormControl>
                  <Input autoComplete="family-name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>Mot de passe</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} autoComplete="new-password" {...field} />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      tabIndex={-1}
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Confirm password */}
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>Confirmer le mot de passe</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" {...field} />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      tabIndex={-1}
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Créer mon compte et rejoindre
        </Button>
      </form>
    </Form>
  );
}
